/**
 * A small xlsx (Office Open XML spreadsheet) writer for the workbooks Teslyn hands out: the master refly list (R24).
 * Teslyn Field and Teslyn Cloud both write it from the same sheets (`@teslyn/domain` `reflyWorkbook`), so the file a
 * crew saves and the one a client downloads are the same. The zip is fflate's (MIT); the parts are the minimum Excel,
 * LibreOffice and Numbers open: content types, relationships, the workbook, styles (bold headings and the number
 * formats the sheets ask for) and one worksheet each, with inline strings, column widths and the heading rows frozen.
 */
import { strToU8, zipSync } from 'fflate';
import type { SheetCell, Workbook, WorkbookSheet } from '@teslyn/domain';

const XML = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
const NS = 'http://schemas.openxmlformats.org/spreadsheetml/2006/main';
const REL = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';

const esc = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    // characters XML 1.0 does not allow
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');

/** 0 → A, 25 → Z, 26 → AA. */
export function columnLetter(i: number): string {
  let n = i + 1;
  let s = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

/** Number formats the workbook uses, given ids from 164 as Excel does for custom formats. */
function formatIds(sheets: readonly WorkbookSheet[]): Map<string, number> {
  const ids = new Map<string, number>();
  for (const s of sheets) for (const f of s.formats) if (f && !ids.has(f)) ids.set(f, 164 + ids.size);
  return ids;
}

/**
 * Cell styles: index 0 the default, then for each format (null first) a plain and a bold style:
 * style(format, bold) = 1 + 2 * formatIndex + (bold ? 1 : 0).
 */
function styles(formats: Map<string, number>): {
  xml: string;
  style: (f: string | null, bold: boolean) => number;
} {
  const list: Array<string | null> = [null, ...formats.keys()];
  const xfs = ['<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>'];
  for (const f of list) {
    const id = f === null ? 0 : formats.get(f)!;
    const fmt = f === null ? '' : ' applyNumberFormat="1"';
    xfs.push(`<xf numFmtId="${id}" fontId="0" fillId="0" borderId="0" xfId="0"${fmt}/>`);
    xfs.push(`<xf numFmtId="${id}" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"${fmt}/>`);
  }
  const numFmts = [...formats.entries()]
    .map(([code, id]) => `<numFmt numFmtId="${id}" formatCode="${esc(code)}"/>`)
    .join('');
  const xml =
    `${XML}<styleSheet xmlns="${NS}">` +
    (formats.size > 0 ? `<numFmts count="${formats.size}">${numFmts}</numFmts>` : '') +
    '<fonts count="2"><font><sz val="11"/><name val="Calibri"/><family val="2"/></font>' +
    '<font><b/><sz val="11"/><name val="Calibri"/><family val="2"/></font></fonts>' +
    '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>' +
    '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
    '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
    `<cellXfs count="${xfs.length}">${xfs.join('')}</cellXfs>` +
    '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
    '</styleSheet>';
  return { xml, style: (f, bold) => 1 + 2 * list.indexOf(f) + (bold ? 1 : 0) };
}

function cellXml(ref: string, v: SheetCell, s: number): string {
  if (v === null || v === '') return '';
  if (typeof v === 'number') return Number.isFinite(v) ? `<c r="${ref}" s="${s}"><v>${v}</v></c>` : '';
  return `<c r="${ref}" s="${s}" t="inlineStr"><is><t>${esc(v)}</t></is></c>`;
}

function sheetXml(sheet: WorkbookSheet, style: (f: string | null, bold: boolean) => number): string {
  const bold = new Set(sheet.bold_rows);
  const cols = sheet.widths
    .map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`)
    .join('');
  const freeze = sheet.header_row + 1;
  const rows = sheet.rows
    .map((row, r) => {
      const cells = row
        .map((v, c) => {
          const isHeading = bold.has(r);
          // headings and titles are text in the general format; data cells take their column's format
          const f = isHeading || r <= sheet.header_row ? null : (sheet.formats[c] ?? null);
          return cellXml(`${columnLetter(c)}${r + 1}`, v, style(f, isHeading));
        })
        .join('');
      return cells ? `<row r="${r + 1}">${cells}</row>` : '';
    })
    .join('');
  return (
    `${XML}<worksheet xmlns="${NS}" xmlns:r="${REL}">` +
    `<sheetViews><sheetView workbookViewId="0"><pane ySplit="${freeze}" topLeftCell="A${freeze + 1}" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>` +
    '<sheetFormatPr defaultRowHeight="15"/>' +
    (cols ? `<cols>${cols}</cols>` : '') +
    `<sheetData>${rows}</sheetData>` +
    '<pageMargins left="0.7" right="0.7" top="0.75" bottom="0.75" header="0.3" footer="0.3"/>' +
    '</worksheet>'
  );
}

/** The bytes of an `.xlsx` file of these sheets. `created` stamps the document properties (ISO time). */
export function writeXlsx(wb: Workbook, created = '2026-01-01T00:00:00Z'): Uint8Array {
  if (wb.sheets.length === 0) throw new Error('A workbook needs at least one sheet.');
  const formats = formatIds(wb.sheets);
  const st = styles(formats);
  const names = wb.sheets.map(
    (s, i) =>
      s.name
        .replace(/[\\/?*[\]:]/g, ' ')
        .slice(0, 31)
        .trim() || `Sheet${i + 1}`,
  );
  const parts: Record<string, string> = {
    '[Content_Types].xml':
      `${XML}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
      '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
      '<Default Extension="xml" ContentType="application/xml"/>' +
      '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
      wb.sheets
        .map(
          (_, i) =>
            `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
        )
        .join('') +
      '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
      '<Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>' +
      '<Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>' +
      '</Types>',
    '_rels/.rels':
      `${XML}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
      `<Relationship Id="rId1" Type="${REL}/officeDocument" Target="xl/workbook.xml"/>` +
      '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>' +
      `<Relationship Id="rId3" Type="${REL}/extended-properties" Target="docProps/app.xml"/>` +
      '</Relationships>',
    'docProps/core.xml':
      `${XML}<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">` +
      `<dc:creator>Teslyn</dc:creator><dcterms:created xsi:type="dcterms:W3CDTF">${esc(created)}</dcterms:created>` +
      '</cp:coreProperties>',
    'docProps/app.xml': `${XML}<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties"><Application>Teslyn</Application></Properties>`,
    'xl/workbook.xml':
      `${XML}<workbook xmlns="${NS}" xmlns:r="${REL}"><sheets>` +
      names.map((n, i) => `<sheet name="${esc(n)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('') +
      '</sheets></workbook>',
    'xl/_rels/workbook.xml.rels':
      `${XML}<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
      wb.sheets
        .map(
          (_, i) =>
            `<Relationship Id="rId${i + 1}" Type="${REL}/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
        )
        .join('') +
      `<Relationship Id="rId${wb.sheets.length + 1}" Type="${REL}/styles" Target="styles.xml"/>` +
      '</Relationships>',
    'xl/styles.xml': st.xml,
  };
  wb.sheets.forEach((s, i) => (parts[`xl/worksheets/sheet${i + 1}.xml`] = sheetXml(s, st.style)));
  const zip: Record<string, Uint8Array> = {};
  for (const [k, v] of Object.entries(parts)) zip[k] = strToU8(v);
  return zipSync(zip, { level: 6, mtime: new Date(created) });
}

/** The content type of an `.xlsx` download. */
export const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
